/**
 * LoadingState Component - Reusable loading indicator using shadcn Skeleton
 * 
 * Provides consistent loading states across the application.
 * Replaces inconsistent loading text patterns.
 * 
 * @example
 * <LoadingState message="Loading properties..." />
 * <LoadingState variant="skeleton" count={5} />
 */
import { memo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingStateProps {
  message?: string;
  variant?: "default" | "skeleton" | "spinner";
  count?: number;
  className?: string;
}

export const LoadingState = memo(function LoadingState({
  message = "Loading...",
  variant = "default", // "default" | "skeleton" | "spinner"
  count = 3,
  className
}: LoadingStateProps) {
  if (variant === "skeleton") {
    return (
      <div className={cn("space-y-4", className)}>
        {Array.from({ length: count }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (variant === "spinner") {
    return (
      <div className={cn("flex flex-col items-center justify-center py-12", className)}>
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
        <p className="text-muted-foreground">{message}</p>
      </div>
    );
  }
  
  return (
    <div className={cn("text-center py-12", className)}>
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
});
